import type { Emitter } from "@core/events/emitter";
import type { DeploymentEntryDto } from "@models/data/deploymentsManifestDto";

/**
 * `IDeploymentsModel` — what else is published on this origin.
 *
 * **Web-only — there is no Unity counterpart.** See
 * `models/data/deploymentsManifestDto.ts` for the manifest, who writes it, and
 * why it is fetched from the origin root rather than through `publicUrl()`.
 *
 * Read by the AI debug panel's **Settings** tab, which lists every published
 * deployment and marks the one the reader is currently looking at. Nothing in
 * the app branches on this — it is a developer read-out, so a failed load
 * degrades to an explanatory line, never to a broken surface.
 *
 * ## Contracts a caller depends on
 *
 * **Reads are synchronous and never fail.** {@link deployments} answers from
 * the cache — empty until the first load lands. Readers learn about changes
 * through {@link onChanged}, which is the same event a re-fetch arrives on, so
 * there is one path to handle. This mirrors `IAppSettingsModel`'s getter
 * contract rather than inventing a second async-state idiom for the panel.
 *
 * **A failed read is reported, not swallowed.** {@link lastError} carries the
 * message so the panel can say WHY the list is empty. "No deployments
 * published" and "the manifest could not be read" are different facts, and a
 * list that renders empty for the second one is actively misleading — in local
 * dev there is no manifest at all, which is the common case.
 *
 * **Loading is explicit, never polled.** A manifest changes only when someone
 * deploys, which no timer can predict — so {@link refreshAsync} re-reads on
 * demand and the panel calls it on every bind (opening the dialog) and from its
 * own Refresh button. {@link ensureLoadedAsync} is the cheaper variant for a
 * caller that only needs the list to exist. Concurrent callers share the
 * in-flight promise either way.
 *
 * Note what a bind-time re-read buys: a tab left open across a deploy is exactly
 * the case the panel's "stale tab" marker exists for, and it cannot be detected
 * from a manifest that was read before that deploy happened.
 */
export interface IDeploymentsModel {
  /** Fires after a load settles, in success and failure alike. */
  readonly onChanged: Emitter<void>;

  /** Published deployments, default first then channels newest-first. */
  readonly deployments: readonly DeploymentEntryDto[];

  /** True once a load has settled — successfully or not. */
  readonly isLoaded: boolean;

  /** True while a fetch is in flight. */
  readonly isLoading: boolean;

  /** Message from the last failed load, else `null`. */
  readonly lastError: string | null;

  /**
   * The entry describing the RUNNING build, matched on
   * `basePath === import.meta.env.BASE_URL`. `null` when the manifest has not
   * loaded, or when this build is not in it — which is itself worth showing: it
   * means the running bundle is older than the manifest (a stale tab), or is
   * being served from somewhere the deploy script did not publish.
   */
  readonly current: DeploymentEntryDto | null;

  /** Loads once. Concurrent callers share the in-flight promise. */
  ensureLoadedAsync(): Promise<void>;

  /** Re-fetches unconditionally. */
  refreshAsync(): Promise<void>;
}

export const DEPLOYMENTS_MODEL = Symbol.for("Campfire.DeploymentsModel");
